import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BASE_URL } from '../apiConfig';

// Helper function for API requests
const fetchData = async (url, jwtBearer) => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${jwtBearer}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Network response was not ok: ${response.status} ${errorText}`);
  }

  return await response.json();
};

// Thunks
// export const fetchAutoRepeatData = createAsyncThunk(
//   'repeatEligibility/fetchAutoRepeatData',
//   async ({ jwtBearer, page, pageSize, filters }, thunkAPI) => {
//     try {
//       const queryParams = new URLSearchParams({
//         page,
//         page_size: pageSize,
//         ...filters
//       }).toString();

//       const result = await fetchData(`${BASE_URL}pipeline/auto-repeat/?${queryParams}`, jwtBearer);
//       return result;
//     } catch (error) {
//       console.error('Fetch error:', error);
//       return thunkAPI.rejectWithValue(error.message);
//     }
//   }
// );

export const fetchAutoRepeatData = createAsyncThunk( 
  'repeatEligibility/fetchAutoRepeatData',
  async ({ jwtBearer, page, pageSize, filters }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        page_size: pageSize,
        ...filters
      }).toString();

      const result = await fetchData(`${BASE_URL}pipeline/auto-repeat/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.reload();
      }
      console.error('Fetch error:', error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);


export const fetchFilteredAutoRepeatData = createAsyncThunk(
  'repeatEligibility/fetchFilteredAutoRepeatData',
  async ({ jwtBearer, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const result = await fetchData(`${BASE_URL}pipeline/auto-repeat/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchFullAppraisalData = createAsyncThunk(
  'repeatEligibility/fetchFullAppraisalData',
  async ({ jwtBearer, page, pageSize, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        page_size: pageSize,
        ...filters
      }).toString();

      const result = await fetchData(`${BASE_URL}pipeline/full-appraisal/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchFilteredFullAppraisalData = createAsyncThunk(
  'repeatEligibility/fetchFilteredFullAppraisalData',
  async ({ jwtBearer, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const result = await fetchData(`${BASE_URL}pipeline/full-appraisal/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchTopUpData = createAsyncThunk(
  'repeatEligibility/fetchTopUpData',
  async ({ jwtBearer, page, pageSize, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        page_size: pageSize,
        ...filters
      }).toString();

      const result = await fetchData(`${BASE_URL}pipeline/top-up/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchFilteredTopUpData = createAsyncThunk(
  'repeatEligibility/fetchFilteredTopUpData',
  async ({ jwtBearer, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const result = await fetchData(`${BASE_URL}pipeline/top-up/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchServicingBAList = createAsyncThunk(
  'repeatEligibility/fetchServicingBAList',
  async ({ jwtBearer, branch_code }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({ branch_code }).toString();
      const result = await fetchData(`${BASE_URL}pipeline/servicing/ba/list/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch BA list error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateServicingBA = createAsyncThunk(
  'repeatEligibility/updateServicingBA',
  async ({ jwtBearer, payload }, thunkAPI) => {
    try {
      const response = await fetch(`${BASE_URL}pipeline/servicing/ba/update`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${jwtBearer}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.code !== 100) {
        return thunkAPI.rejectWithValue(data.message || 'Failed to update servicing BA');
      }
      return data;
    } catch (error) {
      console.error('Update Servicing BA error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Slice
const repeatEligibility = createSlice({
  name: 'repeatEligibility',
  initialState: {
    autoRepeatData: [],
    fullAppraisalData: [],
    topUpData: [],
    filteredAutoRepeatData: [],
    filteredFullAppraisalData: [],
    filteredTopUpData: [],
    totalCountAutoRepeat: 0,
    totalCountFullAppraisal: 0,
    totalCountTopUp:0,
    totalCountFilteredAutoRepeat: 0,
    totalCountFilteredFullAppraisal: 0,
    totalCountFilteredTopUp:0,
    isLoadingAutoRepeat: false,
    isLoadingFullAppraisal: false,
    isLoadingTopUp: false,
    isLoadingFilteredAutoRepeat: false,
    isLoadingFilteredFullAppraisal: false,
    isLoadingFilteredTopUp: false,
    errorAutoRepeat: null,
    errorFullAppraisal: null,
    errorTopUp: null,
    errorFilteredAutoRepeat: null,
    errorFilteredFullAppraisal: null,
    errorFilteredTopUp: null,
    servicingBAList: [],
    isLoadingServicingBAList: false,
    errorServicingBAList: null,
    isUpdatingServicingBA: false,
    updateServicingBAError: null,
    updateServicingBASuccess: null,
  },
  reducers: {
    resetServicingBAState: (state) => {
      state.servicingBAList = [];
      state.errorServicingBAList = null;
      state.updateServicingBAError = null;
      state.updateServicingBASuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Auto Repeat Data
      .addCase(fetchAutoRepeatData.pending, (state) => {
        state.isLoadingAutoRepeat = true;
        state.errorAutoRepeat = null;
      })
      .addCase(fetchAutoRepeatData.fulfilled, (state, action) => {
        state.isLoadingAutoRepeat = false;
        state.autoRepeatData = action.payload.data.auto_repeat || [];
        console.log("state.autoRepeatData", state.autoRepeatData)
        state.totalCountAutoRepeat = action.payload.data.total_auto_repeat || 0;
        console.log(" state.totalCountAutoRepeat", state.totalCountAutoRepeat)
      })
      .addCase(fetchAutoRepeatData.rejected, (state, action) => {
        state.isLoadingAutoRepeat = false;
        state.errorAutoRepeat = action.payload || 'An error occurred while fetching auto repeat data';
      })

      // Full Appraisal Data
      .addCase(fetchFullAppraisalData.pending, (state) => {
        state.isLoadingFullAppraisal = true;
        state.errorFullAppraisal = null;
      })
      .addCase(fetchFullAppraisalData.fulfilled, (state, action) => {
        state.isLoadingFullAppraisal = false;
        state.fullAppraisalData = action.payload.data.full_appraisal || [];
        console.log("state.fullappraisalData", state.fullAppraisalData)
        state.totalCountFullAppraisal = action.payload.data.total_full_appraisal || 0;
        console.log("state.totalCountFullAppraisal", state.totalCountFullAppraisal)
      })
      .addCase(fetchFullAppraisalData.rejected, (state, action) => {
        state.isLoadingFullAppraisal = false;
        state.errorFullAppraisal = action.payload || 'An error occurred while fetching full appraisal data';
      })

      // Top Up Data
      .addCase(fetchTopUpData.pending, (state) => {
        state.isLoadingTopUp = true;
        state.errorTopUp = null;
      })
      .addCase(fetchTopUpData.fulfilled, (state, action) => {
        state.isLoadingTopUp = false;
        state.topUpData = action.payload.data.top_up || [];
        console.log("state.topUpData", state.topUpData)
        state.totalCountTopUp = action.payload.data.total_top_up || 0;
        console.log("state.totalCountTopUp", state.totalCountTopUp)
      })
      .addCase(fetchTopUpData.rejected, (state, action) => {
        state.isLoadingTopUp = false;
        state.errorTopUp = action.payload || 'An error occurred while fetching full appraisal data';
      })


      // Filtered Auto Repeat Data
      .addCase(fetchFilteredAutoRepeatData.pending, (state) => {
        state.isLoadingFilteredAutoRepeat = true;
        state.errorFilteredAutoRepeat = null;
      })
      .addCase(fetchFilteredAutoRepeatData.fulfilled, (state, action) => {
        state.isLoadingFilteredAutoRepeat = false;
        state.filteredAutoRepeatData = action.payload.data.auto_repeat || [];
        console.log("state.filteredAutoRepeatData ", state.filteredAutoRepeatData)
      })
      .addCase(fetchFilteredAutoRepeatData.rejected, (state, action) => {
        state.isLoadingFilteredAutoRepeat = false;
        state.errorFilteredAutoRepeat = action.payload || 'An error occurred while fetching filtered auto repeat data';
      })

      // Filtered Full Appraisal Data
      .addCase(fetchFilteredFullAppraisalData.pending, (state) => {
        state.isLoadingFilteredFullAppraisal = true;
        state.errorFilteredFullAppraisal = null;
      })
      .addCase(fetchFilteredFullAppraisalData.fulfilled, (state, action) => {
        state.isLoadingFilteredFullAppraisal = false;
        state.filteredFullAppraisalData = action.payload.data.full_appraisal || [];
        console.log("state.filteredFullAppraisalData", state.filteredFullAppraisalData);
      })
      .addCase(fetchFilteredFullAppraisalData.rejected, (state, action) => {
        state.isLoadingFilteredFullAppraisal = false;
        state.errorFilteredFullAppraisal = action.payload || 'An error occurred while fetching filtered full appraisal data';
      })

      // Top Up Appraisal Data
      .addCase(fetchFilteredTopUpData.pending, (state) => {
        state.isLoadingTopUp = true;
        state.errorFilteredTopUp = null;
      })
      .addCase(fetchFilteredTopUpData.fulfilled, (state, action) => {
        state.isLoadingTopUp = false;
        state.filteredTopUpData = action.payload.data.top_up || [];
        console.log("state.filteredTopUpData", state.filteredTopUpData);
      })
      .addCase(fetchFilteredTopUpData.rejected, (state, action) => {
        state.isLoadingTopUp = false;
        state.errorFilteredTopUp = action.payload || 'An error occurred while fetching filtered full appraisal data';
      })

      // Servicing BA List
      .addCase(fetchServicingBAList.pending, (state) => {
        state.isLoadingServicingBAList = true;
        state.errorServicingBAList = null;
        state.servicingBAList = [];
      })
      .addCase(fetchServicingBAList.fulfilled, (state, action) => {
        state.isLoadingServicingBAList = false;
        state.servicingBAList = Array.isArray(action.payload?.message) ? action.payload.message : [];
      })
      .addCase(fetchServicingBAList.rejected, (state, action) => {
        state.isLoadingServicingBAList = false;
        state.errorServicingBAList = action.payload || 'An error occurred while fetching servicing BA list';
      })

      // Update Servicing BA
      .addCase(updateServicingBA.pending, (state) => {
        state.isUpdatingServicingBA = true;
        state.updateServicingBAError = null;
        state.updateServicingBASuccess = null;
      })
      .addCase(updateServicingBA.fulfilled, (state, action) => {
        state.isUpdatingServicingBA = false;
        state.updateServicingBASuccess = action.payload?.message || 'Servicing BA updated successfully';
      })
      .addCase(updateServicingBA.rejected, (state, action) => {
        state.isUpdatingServicingBA = false;
        state.updateServicingBAError = action.payload || 'An error occurred while updating servicing BA';
      });

  },
});

export const { resetServicingBAState } = repeatEligibility.actions;

export default repeatEligibility.reducer;
